import { arrayGroup } from "@/core/util/arrayGroup";
import { Modificator, ModificatorOption } from "@/gql/gen";
import { useFeatureStore } from "@/modules/feature/store/featrue";
import { useFortuneStore } from "@/modules/fortune/store/fortune";
import { ProductModel } from "@/modules/shop/composable/useProduct";
import { BasketItemModel } from "@/modules/shop/composable/util";
import { useBasketStore } from "@/modules/shop/store/basket";
import { useCatalogStore } from "@/modules/shop/store/catalog";
import { ModificatorInactiveReason, useProductModificatorStore } from "@/modules/shop/store/product-modificator";
import { RecalculateContext } from "@/modules/shop/store/types/orchestrator";
import { SuggestedModificatorProduct } from "@/modules/shop/store/util/product-modificator";
import { useVorderState } from "@/modules/shop/store/vorder-state";
import debounce from "lodash-es/debounce";
import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";

const STORE_NAME = "product-modificators-computed";

/**
 * Базовое состояние модификатора после префильтра
 * Содержит только результаты проверки условий активности
 */
export interface ModificatorBaseState {
    modificator: Modificator;
    active: boolean;
    inactiveReason?: ModificatorInactiveReason;
}

/**
 * Тип источника товаров модификатора
 * Requirements: 1.1, 2.1
 *
 * - 'static': Товары из статического списка OPTIONS модификатора
 * - 'prize': Товары из активного приза колеса фортуны
 * - 'coupon': Товары из активного купона
 * - 'bonus': Товары из активного бонуса (зарезервировано для будущего использования)
 * - 'discount': Товары из активной скидки (зарезервировано для будущего использования)
 */
export type ProductSource = 'static' | 'prize' | 'coupon' | 'bonus' | 'discount';

/**
 * Виртуальная опция для динамических модификаторов
 * Requirements: 2.1, 2.3
 *
 * Создается автоматически для товаров из внешних источников (prize/coupon/bonus)
 * вместо использования статических OPTIONS модификатора
 */
export interface VirtualModificatorOption extends ModificatorOption {
    /** Уникальный идентификатор виртуальной опции: "{source}_{modId}_{productId}" */
    UID: string;
    /** ID товара из внешнего источника */
    PRODUCT_ID: number;
    /** Всегда 1 для динамических источников (один товар из бенефита) */
    QUANTITY_LIMIT: 1;
    /** Маркер виртуальной опции для отладки */
    _isDynamic?: true;
    /** Источник товара */
    _source?: ProductSource;
}

/**
 * Товар из модификатора с информацией о доступности
 */
export interface ModificatorProduct {
    modificator: Modificator;           // Родительский модификатор
    option: ModificatorOption;          // Опция модификатора
    product: ProductModel;              // Модель товара
    isAvailable?: boolean;              // Доступен ли товар для добавления (false для неактивных по цене)
    currentQuantity: number
    quantityLimit: number
}

/**
 * Группа товаров по модификатору
 */
export interface ModificatorProductGroup {
    modificator: Modificator;           // Модификатор
    products: ModificatorProduct[];     // Товары этого модификатора
}

/**
 * Контекст для проверки условий модификатора
 */
export interface ModificatorConditionContext {
    attrs: any;                         // Атрибуты заказа (payment, transport)
    office?: any;                       // Выбранный офис
    basketStat: any;                    // Статистика корзины
    basketItems: BasketItemModel[];     // Элементы корзины
    orderPriceSource: number;           // Сумма без скидок (корзина + доставка)
    orderPriceTotal: number;            // Сумма со скидками (корзина + доставка) - для ORDER_PRICE_FROM
}

// Экспортируем тип из product-modificator store для обратной совместимости
export type { ModificatorInactiveReason } from "@/modules/shop/store/product-modificator";

/**
 * Выбор gift подарка
 * Перенесено из cart-gift.ts для объединения stores
 */
export interface CartGiftSelection {
    /** ID модификатора */
    modificatorId: number;
    /** ID выбранного товара */
    productId: number;
    /** UID опции модификатора */
    optionUid: string;
    /** GROUP_CODE для группировки */
    groupCode: string;
}

/**
 * Товар модификатора с метаданными
 *
 * Используется для:
 * - Отображения товаров в ModificatorSection
 * - Валидации selectedGifts при обновлении состояний
 * - Единого API для работы со статическими и динамическими товарами
 */
export interface ModificatorProductItem {
    productId: number;              // ID товара
    product?: ProductModel;         // Модель товара (если загружена)
    optionUid: string;              // UID опции модификатора
    source: 'static' | 'dynamic';   // Источник: статический OPTIONS или динамический
    currentQuantity?: number;       // Текущее количество в корзине
    quantityLimit?: number;         // Лимит количества
}

/**
 * Состояние модификатора
 * Расширено для поддержки gift модификаторов (Requirements 8.1, 8.2)
 * и динамических источников товаров (Requirements 1.1, 2.1)
 */
export interface ModificatorState {
    modificator: Modificator;           // Модификатор
    active: boolean;                    // Активен ли модификатор
    inactiveReason?: ModificatorInactiveReason; // Причина неактивности
    progress: number;                   // Прогресс активации (0-1) для ORDER_PRICE_FROM
    priceRemaining: number;             // Сколько не хватает до ORDER_PRICE_FROM
    hasAvailableProducts: boolean;      // Есть ли доступные товары

    // Унифицированный список товаров модификатора
    products: ModificatorProductItem[]; // Все доступные товары (статические + динамические)

    // Новые поля для gift модификаторов
    isGlobalGift?: boolean;             // Является ли глобальным gift модификатором (IS_GLOBAL && TYPE === 'gift')
    selectedGift?: CartGiftSelection;   // Выбранный подарок для этого модификатора (если есть)

    // Поля для динамических источников (Requirements: 1.1, 2.1, 2.3)
    productSource: ProductSource;       // Источник товаров ('static' | 'prize' | 'coupon' | 'bonus' | 'discount')
    sourceEntity: any | null;           // Ссылка на источник (prize/coupon/bonus entity)
}

/**
 * Опции для получения suggestions
 */
export interface SuggestionsOptions {
    /** Включать только глобальные модификаторы (IS_GLOBAL=true) */
    onlyGlobal?: boolean;
    /** Включать только модификаторы из корзины (IS_GLOBAL=false) */
    onlyBasket?: boolean;
    /** Включать неактивные глобальные модификаторы, которым не хватает только суммы заказа */
    includeInactiveByPrice?: boolean;
    /** Включать только модификаторы с доступными товарами */
    onlyWithAvailableProducts?: boolean;
}

/**
 * Результат getModificators (ранее getSuggestions)
 */
export interface SuggestionsResult {
    /** Модификаторы */
    modificators: Modificator[];
    /** Товары (плоский список) */
    products: ModificatorProduct[];
    /** Товары, сгруппированные по модификаторам */
    productsByModificator: ModificatorProductGroup[];
    /** Прогресс активации (для неактивных модификаторов) */
    progress: Record<number, { remaining: number; progress: number }>;
}

/**
 * Pinia store для работы с предложениями модификаторов
 *
 * Предоставляет единый API для получения всех типов предложений:
 * - Активные модификаторы (из корзины и глобальные)
 * - Предлагаемые модификаторы (с доступными товарами)
 * - Доступные товары (плоский список и группировка)
 *
 * Все данные реактивны и автоматически обновляются при изменении корзины
 */
export const useProductModificatorsComputedStore = defineStore(STORE_NAME, () => {
    // Получаем необходимые stores
    const basketStore = useBasketStore();
    const productModificatorStore = useProductModificatorStore();
    const vorderState = useVorderState();
    const catalogStore = useCatalogStore();
    const featureStore = useFeatureStore();
    const fortuneStore = useFortuneStore();

    // Извлекаем реактивные данные
    const basketItems = computed(() => basketStore.basketItemsModels);
    const {productModificators, productModificatorsByProductId} = storeToRefs(productModificatorStore);
    const {checkModificatorCondition} = productModificatorStore;
    const {attrs} = storeToRefs(vorderState);
    const {productsModelsById} = storeToRefs(catalogStore);
    const {featuresActiveById} = storeToRefs(featureStore);

    /**
     * Выбранные подарки, индексированные по GROUP_CODE
     * Перенесено из useCartGiftStore для объединения stores
     * Requirements: 8.1
     */
    const selectedGifts = ref<Record<string, CartGiftSelection>>({});

    /**
     * Выбрать gift подарок
     * Перенесено из useCartGiftStore с валидацией через modificatorsStateMap
     * Requirements: 8.1, 8.5
     *
     * @param groupCode - GROUP_CODE модификатора
     * @param modificatorId - ID модификатора
     * @param productId - ID выбранного товара
     * @param optionUid - UID опции модификатора
     */
    function selectGift(
        groupCode: string,
        modificatorId: number,
        productId: number,
        optionUid: string
    ): void {
        // Валидация через modificatorsStateMap
        const state = modificatorsStateMap.value[modificatorId];
        if (!state || !state.active || !state.isGlobalGift) {
            console.error('[ModificatorsComputedStore] Cannot select gift from inactive modificator', {
                modificatorId,
                state
            });
            return;
        }

        // Проверяем, что товар доступен в этом модификаторе
        const isProductAvailable = state.products.some(item =>
            item.productId === productId && item.optionUid === optionUid
        );

        if (!isProductAvailable) {
            console.error('[ModificatorsComputedStore] Product not available in modificator', {
                modificatorId,
                productId,
                optionUid
            });
            return;
        }

        // Сохраняем выбор
        selectedGifts.value[groupCode] = {
            modificatorId,
            productId,
            optionUid,
            groupCode,
        };
    }

    /**
     * Отменить выбор gift подарка
     * Requirements: 8.1
     *
     * @param groupCode - GROUP_CODE модификатора
     */
    function deselectGift(groupCode: string): void {
        delete selectedGifts.value[groupCode];
    }

    /**
     * Очистить все выборы gift подарков
     * Requirements: 8.1
     */
    function clearAllGifts(): void {
        selectedGifts.value = {};
    }

    /**
     * Очистить неактивные gift подарки
     * Вызывается автоматически при изменении modificatorsStateMap
     *
     * Проверяет:
     * 1. Существует ли модификатор и активен ли он
     * 2. Есть ли выбранный товар среди доступных products модификатора
     *
     * Requirements: 8.5
     */
    function cleanupInactiveGifts(): void {
        for (const [groupCode, selection] of Object.entries(selectedGifts.value)) {
            const state = modificatorsStateMap.value[selection.modificatorId];

            // Проверка 1: Модификатор должен существовать, быть активным и быть gift модификатором
            if (!state || !state.active || !state.isGlobalGift) {
                console.log('[ModificatorsComputedStore] Cleaning up inactive gift', {
                    groupCode,
                    modificatorId: selection.modificatorId,
                    reason: !state ? 'no state' : !state.active ? 'inactive' : 'not global gift'
                });
                delete selectedGifts.value[groupCode];
                continue;
            }

            // Проверка 2: Выбранный товар должен быть среди доступных products
            const isProductAvailable = state.products.some(item =>
                item.productId === selection.productId && item.optionUid === selection.optionUid
            );

            if (!isProductAvailable) {
                console.log('[ModificatorsComputedStore] Cleaning up gift - product not available', {
                    groupCode,
                    modificatorId: selection.modificatorId,
                    productId: selection.productId,
                    optionUid: selection.optionUid,
                    availableProducts: state.products.map(p => p.productId)
                });
                delete selectedGifts.value[groupCode];
            }
        }
    }

    /**
     * НОВАЯ ФУНКЦИЯ: Определение источника товаров модификатора и формирование products
     * Requirements: 1.1, 1.2, 2.1
     *
     * Определяет откуда брать товары для модификатора на основе CONDITION.BENEFIT_TYPE
     * и текущего attrs.BENEFIT_TYPE, и сразу формирует готовый массив ModificatorProductItem:
     * - 'prize': товары из активного приза колеса фортуны (Fortune Store)
     * - 'coupon': товары из активного купона (Vorder State)
     * - 'bonus': товары из активного бонуса (зарезервировано для будущего)
     * - 'static': товары из статического списка OPTIONS модификатора
     *
     * @param modificator - Модификатор для проверки
     * @returns Объект с источником, готовым массивом products и ссылкой на entity
     */
    function resolveProductSource(modificator: Modificator): {
        source: ProductSource;
        products: ModificatorProductItem[];
        entity: any | null;
    } {
        const benefitTypes = modificator.CONDITION?.BENEFIT_TYPE || [];
        const currentBenefitType = attrs.value.BENEFIT_TYPE;

        // Проверяем, есть ли текущий BENEFIT_TYPE в условиях модификатора
        if (currentBenefitType && benefitTypes.includes(currentBenefitType)) {
            // Определяем источник на основе BENEFIT_TYPE
            switch (currentBenefitType) {
                case 'prize': {
                    // Импортируем Fortune Store динамически для избежания циклических зависимостей
                    try {
                        const prize = fortuneStore.userPrizeElement;
                        const product = fortuneStore.userPrizeGiftProduct;

                        // Проверяем, что приз существует и это приз типа 'gift' (а не 'discount')
                        // PRIZE_TYPE может быть 'gift' или 'discount' (PrizeTypeEnum)
                        if (prize?.PROPERTIES?.PRIZE_TYPE === 'gift' && product) {
                            return {
                                source: 'prize',
                                products: [{
                                    productId: product.ID,
                                    product: product as ProductModel,
                                    optionUid: `dynamic_${product.ID}`,
                                    source: 'dynamic',
                                }],
                                entity: prize
                            };
                        }
                    } catch (e) {
                        console.warn('[ModificatorsComputedStore] Failed to load Fortune Store:', e);
                    }
                    break;
                }

                case 'coupon': {
                    const coupon = vorderState.coupon;

                    // Получаем товар из купона
                    // Coupon имеет поля PRODUCT_ID и PRODUCT (связанный товар)
                    if (coupon) {
                        // Сначала пробуем получить товар напрямую из поля PRODUCT
                        if (coupon.PRODUCT) {
                            const product = productsModelsById.value[coupon.PRODUCT.ID];
                            if (product) {
                                return {
                                    source: 'coupon',
                                    products: [{
                                        productId: product.ID,
                                        product: product,
                                        optionUid: `dynamic_${product.ID}`,
                                        source: 'dynamic',
                                    }],
                                    entity: coupon
                                };
                            }
                        }

                        // Если PRODUCT нет, пробуем получить по PRODUCT_ID
                        if (coupon.PRODUCT_ID) {
                            const product = productsModelsById.value[coupon.PRODUCT_ID];
                            if (product) {
                                return {
                                    source: 'coupon',
                                    products: [{
                                        productId: product.ID,
                                        product: product,
                                        optionUid: `dynamic_${product.ID}`,
                                        source: 'dynamic',
                                    }],
                                    entity: coupon
                                };
                            }
                        }
                    }
                    break;
                }

                // 'bonus' и другие типы пока не поддерживаются
                // Будут добавлены позже при необходимости
                case 'bonus':
                case 'discount':
                    // Пока не реализовано, fallback на static
                    break;
            }
        }

        // Fallback: используем статические OPTIONS (для всех случаев, когда динамический источник не найден)
        const products: ModificatorProductItem[] = [];
        for (const option of modificator.OPTIONS) {
            if (option.PRODUCT_ID) {
                products.push({
                    productId: option.PRODUCT_ID,
                    product: productsModelsById.value[option.PRODUCT_ID],
                    optionUid: option.UID,
                    source: 'static',
                });
            }
        }
        return {source: 'static', products, entity: null};
    }

    /**
     * Базовые состояния модификаторов (результат префильтра)
     * Обновляется через recalculateActiveModificators из оркестратора
     */
    const modificatorsBaseStates = ref<ModificatorBaseState[]>([])

    /**
     * Функция для пересчета базовых состояний модификаторов (префильтр)
     * Вызывается из оркестратора (useVorderStore)
     *
     * Проверяет только базовые условия и сохраняет результат:
     * - ACTIVE === true
     * - Все FEATURES активны
     * - Условия модификатора (через checkModificatorCondition)
     * - Сохраняет inactiveReason для неактивных модификаторов
     *
     * @param context - Контекст с данными из оркестратора
     */
    const recalculateActiveModificators = (context: RecalculateContext = {}) => {
        // ВАЖНО: Контекст должен быть передан из оркестратора
        if (!context.restrictionContext) {
            console.warn('[ModificatorsComputedStore] recalculateActiveModificators called without restrictionContext')
            return
        }

        // Используем restrictionContext из контекста
        const modConditionContext: ModificatorConditionContext = {
            attrs: context.restrictionContext.attrs,
            office: context.restrictionContext.office,
            basketStat: context.restrictionContext.basketStat,
            basketItems: context.restrictionContext.basketItems,
            orderPriceSource: context.restrictionContext.orderPriceSource ?? 0,
            orderPriceTotal: context.restrictionContext.orderPriceTotal ?? 0,
        };

        const result: ModificatorBaseState[] = [];

        // Обрабатываем все модификаторы
        for (const modificator of productModificators.value) {
            let isActive = false;
            let inactiveReason: ModificatorInactiveReason | undefined = undefined;

            // Шаг 1: Проверяем базовую активность модификатора
            if (!modificator.ACTIVE) {
                inactiveReason = 'inactive';
            }
            // Шаг 2: Проверяем активность всех фичей модификатора
            else if (modificator.FEATURES && modificator.FEATURES.length > 0) {
                const allFeaturesActive = modificator.FEATURES.every(featureId => {
                    return featureId && featuresActiveById.value[featureId];
                });
                if (!allFeaturesActive) {
                    inactiveReason = 'feature';
                }
            }

            // Шаг 3: Если базовые проверки пройдены, проверяем условия через checkModificatorCondition
            if (!inactiveReason) {
                const checkResult = checkModificatorCondition(modificator, modConditionContext);
                isActive = checkResult.isActive;
                if (!isActive) {
                    inactiveReason = checkResult.inactiveReason || 'inactive';
                }
            }

            // Сохраняем результат для всех модификаторов (активных и неактивных)
            result.push({
                modificator,
                active: isActive,
                inactiveReason,
            });
        }

        // Обновляем ref
        modificatorsBaseStates.value = result;
    };

    // Debounced версия для использования в оркестраторе
    const debouncedRecalculateActiveModificators = debounce(recalculateActiveModificators, 300);

    /**
     * Активные модификаторы (для обратной совместимости)
     * Computed который фильтрует только активные из modificatorsBaseStates
     */
    const activeModificators = computed<Modificator[]>(() => {
        return modificatorsBaseStates.value
            .filter(state => state.active)
            .map(state => state.modificator);
    });

    /**
     * Карта состояний всех модификаторов
     * Computed который использует modificatorsBaseStates и дополняет данные:
     * - progress и priceRemaining для ORDER_PRICE_FROM
     * - products (статические + динамические)
     * - hasAvailableProducts
     * - isGlobalGift и selectedGift
     * - productSource и sourceEntity
     */
    const modificatorsStateMap = computed<Record<number, ModificatorState>>(() => {
        const result: Record<number, ModificatorState> = {};

        console.log('dddffffff')

        // Используем basketPrice для расчета прогресса
        const basketTotal = basketStore.basketPrice ?? 0;

        // Обрабатываем все модификаторы из базовых состояний
        for (const baseState of modificatorsBaseStates.value) {
            const modificator = baseState.modificator;
            const isActive = baseState.active;
            const inactiveReason = baseState.inactiveReason;

            // Вычисляем прогресс для ORDER_PRICE_FROM
            let progress = 0;
            let priceRemaining = 0;

            if (isActive) {
                // Активный модификатор имеет прогресс 1
                progress = 1;
            } else if (inactiveReason === 'order-price' && modificator.ORDER_PRICE_FROM) {
                priceRemaining = modificator.ORDER_PRICE_FROM - basketTotal;
                progress = Math.min(1, Math.max(0, basketTotal / modificator.ORDER_PRICE_FROM));
            }

            // Определяем источник товаров и получаем готовый массив products
            const {source, products, entity: sourceEntity} = resolveProductSource(modificator);

            // Проверяем наличие доступных товаров
            const hasAvailableProducts = products.length > 0;

            // Логика для gift модификаторов
            const isGlobalGift = modificator.IS_GLOBAL && modificator.TYPE === 'gift';
            let selectedGift: CartGiftSelection | undefined = undefined;

            if (isGlobalGift) {
                // Получаем выбранный подарок для этой группы
                selectedGift = selectedGifts.value[modificator.GROUP_CODE];
            }

            result[modificator.ID] = {
                modificator,
                active: isActive,
                inactiveReason,
                progress,
                priceRemaining: Math.max(0, priceRemaining),
                hasAvailableProducts,

                // Унифицированный список товаров
                products,

                // Поля для gift модификаторов
                isGlobalGift,
                selectedGift,

                // Поля для динамических источников
                productSource: source,
                sourceEntity: sourceEntity,
            };
        }

        return result;
    });

    /**
     * Карта состояний модификаторов по ID товара-опции
     * Ключ - ID товара, значение - массив состояний модификаторов для этого товара
     *
     * ПЕРЕИМЕНОВАНО: modificatorsStateByProductId → modificatorsStateByOptionProductId
     * для более точного отражения назначения (товары, являющиеся опциями модификаторов)
     *
     * Используется для быстрого получения всех модификаторов товара
     * в карточках товаров
     */
    const modificatorsStateByOptionProductId = computed<Record<number, ModificatorState[]>>(() => {
        const result: Record<number, ModificatorState[]> = {};

        // Проходим по всем модификаторам и их опциям
        for (const state of Object.values(modificatorsStateMap.value)) {
            for (const option of state.modificator.OPTIONS) {
                if (!option.PRODUCT_ID) continue;

                if (!result[option.PRODUCT_ID]) {
                    result[option.PRODUCT_ID] = [];
                }

                const productStates = result[option.PRODUCT_ID];
                // Добавляем состояние модификатора только если его еще нет
                if (productStates && !productStates.some(s => s.modificator.ID === state.modificator.ID)) {
                    productStates.push(state);
                }
            }
        }

        // Также добавляем модификаторы, привязанные к товарам через productModificatorsByProductId
        for (const [productIdStr, modificators] of Object.entries(productModificatorsByProductId.value)) {
            const productId = parseInt(productIdStr);
            if (!result[productId]) {
                result[productId] = [];
            }

            for (const modificator of modificators) {
                const state = modificatorsStateMap.value[modificator.ID];
                const productStates = result[productId];
                if (state && productStates && !productStates.some(s => s.modificator.ID === modificator.ID)) {
                    productStates.push(state);
                }
            }
        }

        return result;
    });

    /**
     * Все модификаторы, соответствующие текущему состоянию заказа
     *
     * Формируется на основе modificatorsStateMap - берем только активные модификаторы.
     * Логика проверки активности находится в modificatorsStateMap.
     *
     * ВАЖНО: Это предфильтр для всех остальных списков
     */
    const applicableModificators = computed<Modificator[]>(() => {
        return Object.values(modificatorsStateMap.value)
            .filter(state => state.active)
            .map(state => state.modificator);
    });

    /**
     * Модификаторы, активные через товары в корзине
     *
     * Берем из applicableModificators только те, которые:
     * - Привязаны к товарам в корзине (через Product.MODIFICATORS или Section.MODIFICATORS)
     * - НЕ являются глобальными (IS_GLOBAL=false)
     */
    const activeBasketModificators = computed<Modificator[]>(() => {
        // Оптимизация: пустая корзина = пустой результат
        if (basketItems.value.length === 0) return [];

        const modIds = new Set<number>();

        // Шаг 1: Собираем ID модификаторов из товаров в корзине
        for (const item of basketItems.value) {
            // Пропускаем товары, добавленные через модификаторы
            if ((item as BasketItemModel).getIsAddedByModificator()) continue;

            const productMods = productModificatorsByProductId.value[(item as BasketItemModel).product.ID];
            if (!productMods?.length) continue;

            for (const mod of productMods) {
                modIds.add(mod.ID);
            }
        }

        // Шаг 2-3: Фильтруем applicableModificators
        return applicableModificators.value.filter(mod => {
            // Должен быть привязан к товарам в корзине
            if (!modIds.has(mod.ID)) return false;

            // Не должен быть глобальным
            if (mod.IS_GLOBAL) return false;

            return true;
        });
    });

    /**
     * Глобальные модификаторы, активные по условиям
     *
     * Берем из applicableModificators только те, которые:
     * - Являются глобальными (IS_GLOBAL=true)
     *
     * ВАЖНО: Условия (включая ORDER_PRICE_FROM) уже проверены в applicableModificators
     */
    const activeGlobalModificators = computed<Modificator[]>(() => {
        return applicableModificators.value.filter(mod => mod.IS_GLOBAL);
    });

    /**
     * Активные глобальные gift модификаторы
     * Используется для формирования availableGifts
     * Requirements: 8.4 (backward compatibility)
     */
    const activeGlobalGiftModificators = computed<Modificator[]>(() => {
        return activeGlobalModificators.value.filter(mod => mod.TYPE === 'gift');
    });

    /**
     * Доступные подарки для выбора, сгруппированные по GROUP_CODE
     * Перенесено из useCartGiftStore для обратной совместимости
     *
     * Возвращает объект, где ключи - GROUP_CODE, а значения содержат:
     * - groupCode: string - код группы
     * - groupName: string - название группы (объединение названий модификаторов через ', ')
     * - products: Array<{modId, productId}> - все уникальные товары из модификаторов группы
     *
     * Requirements: 8.4 (backward compatibility)
     */
    const availableGifts = computed(() => {
        // Группируем по GROUP_CODE
        const grouped = arrayGroup(
            activeGlobalGiftModificators.value,
            (mod: Modificator) => mod.GROUP_CODE
        );

        const result: Record<string, {
            groupCode: string;
            groupName: string;
            products: Array<{ modId: number; productId: number }>;
        }> = {};

        // Для каждой группы формируем структуру
        for (const [groupCode, mods] of Object.entries(grouped)) {
            // Собираем названия модификаторов для группы
            const groupName = (mods as Modificator[]).map((m: Modificator) => m.NAME).join(', ');

            // Собираем все уникальные товары из всех модификаторов группы
            const productsMap = new Map<number, { modId: number; productId: number }>();

            for (const mod of (mods as Modificator[])) {
                const state = modificatorsStateMap.value[mod.ID];
                if (!state) continue;

                // Используем унифицированный массив products
                for (const productItem of state.products) {
                    productsMap.set(productItem.productId, {
                        modId: mod.ID,
                        productId: productItem.productId,
                    });
                }
            }

            result[groupCode] = {
                groupCode,
                groupName,
                products: Array.from(productsMap.values()),
            };
        }

        return result;
    });

    /**
     * Объединенный список всех активных модификаторов
     */
    const allActiveModificators = computed<Modificator[]>(() => {
        const seen = new Set<number>();
        const result: Modificator[] = [];

        // Сначала добавляем модификаторы из корзины
        for (const mod of activeBasketModificators.value) {
            if (!seen.has(mod.ID)) {
                seen.add(mod.ID);
                result.push(mod);
            }
        }

        // Затем добавляем глобальные модификаторы
        for (const mod of activeGlobalModificators.value) {
            if (!seen.has(mod.ID)) {
                seen.add(mod.ID);
                result.push(mod);
            }
        }

        return result;
    });


    /**
     * Все глобальные модификаторы (IS_GLOBAL=true)
     * Без фильтрации по условиям
     */
    const globalModificators = computed<Modificator[]>(() => {
        return productModificators.value.filter(mod => mod.IS_GLOBAL);
    });

    /**
     * Неактивные глобальные модификаторы (условия не выполнены)
     */
    const inactiveGlobalModificators = computed<Modificator[]>(() => {
        const activeIds = new Set(activeGlobalModificators.value.map(m => m.ID));
        return globalModificators.value.filter(mod => !activeIds.has(mod.ID));
    });

    /**
     * Прогресс активации неактивных глобальных модификаторов
     * Показывает сколько не хватает до ORDER_PRICE_FROM
     * Теперь берет данные из modificatorsStateMap вместо вычисления
     */
    const inactiveModificatorsProgress = computed<Record<number, { remaining: number; progress: number }>>(() => {
        const result: Record<number, { remaining: number; progress: number }> = {};

        for (const mod of inactiveGlobalModificators.value) {
            const state = modificatorsStateMap.value[mod.ID];
            if (!state || !mod.ORDER_PRICE_FROM) continue;

            result[mod.ID] = {
                remaining: state.priceRemaining,
                progress: state.progress,
            };
        }

        return result;
    });

    /**
     * Универсальный метод для получения модификаторов и их товаров
     * Полностью основан на предвычисленных данных из modificatorsStateMap
     *
     * @param options - опции фильтрации
     * @returns объект с модификаторами, товарами и прогрессом
     *
     * Примеры использования:
     * - Только активные глобальные: { onlyGlobal: true }
     * - Глобальные + неактивные по цене: { onlyGlobal: true, includeInactiveByPrice: true }
     * - Только из корзины с товарами: { onlyBasket: true, onlyWithAvailableProducts: true }
     *
     * ПЕРЕИМЕНОВАНО: getSuggestions → getModificators для более точного названия
     */
    function getModificators(options: SuggestionsOptions = {}): SuggestionsResult {
        const {
            onlyGlobal = false,
            onlyBasket = false,
            includeInactiveByPrice = false,
            onlyWithAvailableProducts = false,
        } = options;

        // Шаг 1: Определяем базовый список модификаторов на основе modificatorsStateMap
        let modificators: Modificator[] = [];

        if (onlyGlobal) {
            // Только глобальные модификаторы
            modificators = [...activeGlobalModificators.value];

            // Добавляем неактивные по цене, если нужно
            if (includeInactiveByPrice) {
                for (const mod of inactiveGlobalModificators.value) {
                    const state = modificatorsStateMap.value[mod.ID];
                    // Включаем только если причина неактивности - ORDER_PRICE_FROM
                    if (state && state.inactiveReason === 'order-price') {
                        modificators.push(mod);
                    }
                }
            }
        } else if (onlyBasket) {
            // Только модификаторы из корзины
            modificators = [...activeBasketModificators.value];
        } else {
            // Все активные модификаторы
            modificators = [...allActiveModificators.value];

            // Добавляем неактивные по цене глобальные для preview
            if (includeInactiveByPrice) {
                for (const mod of inactiveGlobalModificators.value) {
                    const state = modificatorsStateMap.value[mod.ID];
                    if (state && state.inactiveReason === 'order-price') {
                        modificators.push(mod);
                    }
                }
            }
        }

        // Шаг 2: Фильтруем по наличию доступных товаров из modificatorsStateMap
        if (onlyWithAvailableProducts) {
            modificators = modificators.filter(modificator => {
                const state = modificatorsStateMap.value[modificator.ID];
                return state?.hasAvailableProducts || false;
            });
        }

        // Шаг 3: Собираем товары из modificatorsStateMap
        const products: ModificatorProduct[] = [];

        for (const modificator of modificators) {
            const state = modificatorsStateMap.value[modificator.ID];
            if (!state) continue;

            // Используем предвычисленные товары из state.products
            for (const productItem of state.products) {
                if (!productItem.product) continue;

                // Находим соответствующую опцию
                const option = modificator.OPTIONS.find(opt => opt.UID === productItem.optionUid);
                if (!option) continue;

                products.push({
                    modificator,
                    option,
                    product: productItem.product,
                    isAvailable: state.active,
                    currentQuantity: productItem.currentQuantity || 0,
                    quantityLimit: productItem.quantityLimit || Infinity,
                });
            }
        }

        // Шаг 4: Группируем товары по модификаторам
        const productsByModificator: ModificatorProductGroup[] = [];
        for (const modificator of modificators) {
            const modProducts = products.filter(p => p.modificator.ID === modificator.ID);
            if (modProducts.length > 0) {
                productsByModificator.push({
                    modificator,
                    products: modProducts,
                });
            }
        }

        // Шаг 5: Собираем прогресс из modificatorsStateMap
        const progress: Record<number, { remaining: number; progress: number }> = {};
        if (includeInactiveByPrice) {
            for (const mod of modificators) {
                const state = modificatorsStateMap.value[mod.ID];
                if (state && state.inactiveReason === 'order-price') {
                    progress[mod.ID] = {
                        remaining: state.priceRemaining,
                        progress: state.progress,
                    };
                }
            }
        }

        return {
            modificators,
            products,
            productsByModificator,
            progress,
        };
    }

    /**
     * Suggestions для глобальных модификаторов (только активные)
     */
    const globalSuggestions = computed(() => {
        return getModificators({
            onlyGlobal: true,
            onlyWithAvailableProducts: true,
        });
    });

    /**
     * Suggestions для модификаторов из корзины
     */
    const basketSuggestions = computed(() => {
        return getModificators({
            onlyBasket: true,
            onlyWithAvailableProducts: true,
        });
    });

    /**
     * Все suggestions (корзина + глобальные)
     */
    const allSuggestions = computed(() => {
        return getModificators({
            onlyWithAvailableProducts: true,
        });
    });

    /**
     * Универсальный метод для получения товаров с фильтрацией
     * Полностью основан на предвычисленных данных из modificatorsStateMap
     *
     * @param filters - Условия фильтрации
     * @returns Массив товаров в формате SuggestedModificatorProduct
     *
     * ПЕРЕИМЕНОВАНО: getUpsaleProducts → getProducts для более универсального названия
     */
    function getProducts(filters: {
        modActive?: boolean;           // Только активные модификаторы
        modTypes?: string[];           // Типы модификаторов ['gift', 'special', 'constructor']
        modGlobal?: boolean;           // Только глобальные (true) или только из корзины (false)
        inBasket?: boolean;            // Товар в корзине (true) или не в корзине (false)
        includeInactiveByPrice?: boolean; // Включать неактивные по цене
    } = {}): SuggestedModificatorProduct[] {
        const {
            modActive = true,
            modTypes,
            modGlobal,
            inBasket,
            includeInactiveByPrice = false,
        } = filters;

        const result: SuggestedModificatorProduct[] = [];

        // Определяем список модификаторов из modificatorsStateMap
        let modificators: Modificator[] = [];

        if (modGlobal === true) {
            // Только глобальные
            modificators = [...activeGlobalModificators.value];
            if (includeInactiveByPrice) {
                for (const mod of inactiveGlobalModificators.value) {
                    const state = modificatorsStateMap.value[mod.ID];
                    if (state && state.inactiveReason === 'order-price') {
                        modificators.push(mod);
                    }
                }
            }
        } else if (modGlobal === false) {
            // Только из корзины
            modificators = [...activeBasketModificators.value];
        } else {
            // Все активные
            modificators = [...allActiveModificators.value];
            if (includeInactiveByPrice) {
                for (const mod of inactiveGlobalModificators.value) {
                    const state = modificatorsStateMap.value[mod.ID];
                    if (state && state.inactiveReason === 'order-price') {
                        modificators.push(mod);
                    }
                }
            }
        }

        // Фильтруем по типам модификаторов
        if (modTypes && modTypes.length > 0) {
            modificators = modificators.filter(mod => mod.TYPE && modTypes.includes(mod.TYPE));
        }

        // Фильтруем по активности
        if (modActive) {
            const activeIds = new Set(applicableModificators.value.map(m => m.ID));
            modificators = modificators.filter(mod => {
                // Либо активен, либо неактивен только по цене (если includeInactiveByPrice=true)
                if (activeIds.has(mod.ID)) return true;
                if (includeInactiveByPrice) {
                    const state = modificatorsStateMap.value[mod.ID];
                    return state?.inactiveReason === 'order-price';
                }
                return false;
            });
        }

        // Собираем товары из modificatorsStateMap
        for (const modificator of modificators) {
            const state = modificatorsStateMap.value[modificator.ID];
            if (!state || state.products.length === 0) continue;

            for (const productItem of state.products) {
                if (!productItem.product) continue;

                // Фильтр по наличию в корзине
                if (inBasket !== undefined) {
                    const productInBasket = basketItems.value.some(
                        (item: BasketItemModel) => item.product.ID === productItem.productId
                    );
                    if (inBasket && !productInBasket) continue;
                    if (!inBasket && productInBasket) continue;
                }

                // Получаем опцию из modificatorsStateMap
                let option = modificator.OPTIONS.find(opt => opt.UID === productItem.optionUid);

                // Для динамических товаров создаем виртуальную опцию
                if (!option && productItem.source === 'dynamic') {
                    option = {
                        UID: productItem.optionUid,
                        PRODUCT_ID: productItem.productId,
                        QUANTITY_LIMIT: 1,
                        _isDynamic: true,
                        _source: state.productSource,
                    } as any;
                }

                if (!option) continue;

                result.push({
                    option,
                    product: productItem.product,
                    modificator,
                    isAvailable: state.active,
                    priceRemaining: state.priceRemaining > 0 ? state.priceRemaining : undefined,
                    progressPercent: state.progress < 1 ? state.progress * 100 : undefined,
                });
            }
        }

        return result;
    }

    return {
        // Базовая фильтрация
        applicableModificators,

        // Списки модификаторов
        activeBasketModificators,
        activeGlobalModificators,
        allActiveModificators: allActiveModificators,

        // Глобальные модификаторы (для обратной совместимости)
        globalModificators,
        inactiveGlobalModificators,
        inactiveModificatorsProgress,

        // Универсальные методы для получения данных
        getModificators,  // ПЕРЕИМЕНОВАНО: getSuggestions → getModificators
        getProducts,      // ПЕРЕИМЕНОВАНО: getUpsaleProducts → getProducts

        // Специализированные suggestions (используют getModificators)
        globalSuggestions,
        basketSuggestions,
        allSuggestions,

        // Карты состояний модификаторов (новое API)
        modificatorsStateMap,
        modificatorsStateByOptionProductId,

        // Gift подарки (Requirements: 8.1, 8.4)
        selectedGifts,
        selectGift,
        deselectGift,
        clearAllGifts,
        cleanupInactiveGifts,
        availableGifts, // Для обратной совместимости с useCartGiftStore

        // Обратная совместимость (deprecated, используйте getModificators и getProducts)
        getSuggestions: getModificators,  // @deprecated используйте getModificators
        getUpsaleProducts: getProducts,   // @deprecated используйте getProducts

        // Функции пересчета для оркестратора
        recalculateActiveModificators,
        debouncedRecalculateActiveModificators,
    };
});
